/**
 * Prozessweiter Store: Hero-Ausfall, erkannt über fehlgeschlagene API-Calls.
 * Wird vom apiClient befüllt; Banner liest per useSyncExternalStore.
 */

type Listener = () => void;

let heroOutageActive = false;
const listeners = new Set<Listener>();

function emit(): void {
  for (const listener of listeners) listener();
}

export function isHeroOutageActive(): boolean {
  return heroOutageActive;
}

export function subscribeHeroOutage(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Pfad relativ zur API (mit optionalem Query) oder absolute URL. */
export function isHeroRelatedApiPath(path: string): boolean {
  let pathname = path;
  try {
    if (/^https?:\/\//i.test(path)) {
      pathname = new URL(path).pathname;
    }
  } catch {
    /* ignore */
  }
  const q = pathname.indexOf("?");
  if (q >= 0) pathname = pathname.slice(0, q);
  const p = pathname.toLowerCase();

  if (p === "/hero" || p.startsWith("/hero/")) return true;
  if (p === "/me/hero-profile" || p.startsWith("/me/hero-profile/")) return true;
  if (p.includes("/orders/hero")) return true;
  if (p.includes("/dokumente/hero")) return true;
  if (p.includes("hero-usage")) return true;
  if (p.includes("hero-calendar")) return true;
  if (/\/auftraege\/[^/]+\/hero/.test(p)) return true;
  if (/\/timesheets\/[^/]+\/hero/.test(p)) return true;
  if (p.includes("/hero-")) return true;
  return false;
}

function isHeroFailureStatus(status: number): boolean {
  return status === 0 || status === 502 || status === 503 || status === 504;
}

function isHeroSuccessStatus(status: number): boolean {
  return status >= 200 && status < 300;
}

/**
 * Ergebnis eines API-Calls melden.
 * - Hero-Pfad + 502/503/504/Timeout → Banner an
 * - Header X-Ernsthaus-Hero-Degraded: 1 (auch bei 200 Soft-Fail) → Banner an
 * - Erfolgreicher Hero-Call ohne Degraded-Header → Banner aus
 */
export function reportHeroApiOutcome(
  path: string,
  status: number,
  opts?: { heroDegraded?: boolean },
): void {
  const heroPath = isHeroRelatedApiPath(path);
  const degraded = opts?.heroDegraded === true;

  if (degraded || (heroPath && isHeroFailureStatus(status))) {
    if (!heroOutageActive) {
      heroOutageActive = true;
      emit();
    }
    return;
  }

  if (heroPath && isHeroSuccessStatus(status) && heroOutageActive) {
    heroOutageActive = false;
    emit();
  }
}

/** Nur für Tests. */
export function __resetHeroOutageStoreForTests(): void {
  heroOutageActive = false;
  listeners.clear();
}
